// Three tones only: breach for withheld/unlawful, amber for suppressed or
// significant, neutral ground for minor. No icons — the label carries the meaning.
const SEVERITY_STYLE = {
  originator_withheld: { class: 'bg-breach-wash text-breach-ink', label: 'Withheld by the author' },
  potentially_unlawful: { class: 'bg-breach-wash text-breach-ink', label: 'Possibly unlawful' },
  opinion_suppressed: { class: 'bg-amber-wash text-amber-ink', label: 'Opinion removed' },
  known_party_withheld: { class: 'bg-amber-wash text-amber-ink', label: 'Someone you know withheld' },
  significant: { class: 'bg-amber-wash text-amber-ink', label: 'Significant' },
  minor: { class: 'bg-ground text-ink-70', label: 'Minor' },
};

const FINDING_TYPE_LABEL = {
  resolved_from_secondary: 'Restored',
  unresolved: 'Unresolved',
  primary_missing: 'Missing in one copy',
  discrepancy: 'Discrepancy',
  originator_withheld: 'Withheld by the author',
};

const CONTENT_TYPE_LABEL = {
  professional_name: 'Professional name',
  social_worker_opinion: 'Social-worker opinion',
  known_third_party: 'Known third party',
  child_name: 'Child name',
  address: 'Address',
  medical_information: 'Medical information',
  meeting_attendees: 'Meeting attendees',
  entire_document_or_section: 'Entire document or section',
  unknown: 'Unknown',
};

const Pill = ({ children, strong }) => (
  <span className={`inline-flex items-center rounded-full bg-surface/70 px-[11px] py-1 text-[13px] ${strong ? 'font-semibold' : 'font-medium'}`}>
    {children}
  </span>
);

export const RadarFindingsList = ({ findings }) => {
  if (!findings || findings.length === 0) {
    return (
      <p className="text-ink-55">
        No findings. The blacked-out text and the content matched cleanly across copies.
      </p>
    );
  }

  // Sort by severity importance
  const order = ['originator_withheld', 'potentially_unlawful', 'opinion_suppressed', 'known_party_withheld', 'significant', 'minor'];
  const sorted = [...findings].sort(
    (a, b) => order.indexOf(a.severity || 'minor') - order.indexOf(b.severity || 'minor')
  );

  return (
    <div className="space-y-2.5" data-testid="radar-findings-list">
      {sorted.map((f, i) => {
        const sev = SEVERITY_STYLE[f.severity] || SEVERITY_STYLE.minor;
        return (
          <article
            key={i}
            className={`rounded-lg p-5 space-y-3 ${sev.class}`}
            data-testid={`radar-finding-${i}`}
          >
            <div className="space-y-1.5">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-heading font-semibold text-[17px] tracking-[-0.01em]">{sev.label}</span>
                <Pill>{FINDING_TYPE_LABEL[f.finding_type] || f.finding_type}</Pill>
                {f.challenge_recommended && <Pill strong>Worth challenging</Pill>}
                {f.disproportionate_redaction && <Pill>Disproportionate</Pill>}
              </div>
              {f.position_description && (
                <p className="text-[15px] opacity-90">{f.position_description}</p>
              )}
            </div>

            <dl className="grid grid-cols-[auto_1fr] gap-x-5 gap-y-1 text-[15px]">
              {f.deduced_content_type && (
                <>
                  <dt className="font-medium">Likely content</dt>
                  <dd>{CONTENT_TYPE_LABEL[f.deduced_content_type] || f.deduced_content_type}</dd>
                </>
              )}
              {f.legal_basis_cited && (
                <>
                  <dt className="font-medium">Basis they cited</dt>
                  <dd className="pd-mono normal-case">{f.legal_basis_cited}</dd>
                </>
              )}
              {f.restored_from_org && (
                <>
                  <dt className="font-medium">Restored from</dt>
                  <dd>{f.restored_from_org}</dd>
                </>
              )}
              {(f.words_redacted_estimate || f.words_restored) && (
                <>
                  <dt className="font-medium">Words</dt>
                  <dd>
                    {f.words_restored ? `${f.words_restored} restored` : ''}
                    {f.words_redacted_estimate ? `${f.words_restored ? ' of ' : ''}about ${f.words_redacted_estimate} removed` : ''}
                  </dd>
                </>
              )}
              <dt className="font-medium">Reason given</dt>
              <dd>{f.justification_provided ? 'Yes' : 'None'}</dd>
            </dl>

            {f.justification_text && (
              <blockquote className="text-[15px] italic bg-surface/70 rounded-md px-4 py-3">
                “{f.justification_text}”
              </blockquote>
            )}

            {f.restored_text && (
              <div className="bg-surface/70 rounded-md px-4 py-3">
                <p className="pd-mono text-ink-55 mb-1.5">Restored text</p>
                <p className="text-[16px] leading-relaxed whitespace-pre-wrap text-ink">{f.restored_text}</p>
              </div>
            )}

            {f.challenge_reason && (
              <p className="text-[15px] leading-relaxed">
                <span className="font-medium">Why you could challenge this:</span> {f.challenge_reason}
              </p>
            )}
          </article>
        );
      })}
    </div>
  );
};

export default RadarFindingsList;
